// Qué le pasó al clip seleccionado, en texto — ADR-2026-11-03-los-errores-del-estudio-se-ven.
//
// Antes el motivo de un error vivía en el `title` del badge o del botón Reintentar:
// sólo se veía pasando el mouse y en el teléfono nunca. Se muestra al seleccionar el clip,
// en el mismo lugar que el aviso de guardado, y se puede cerrar.
import { useRef } from 'react';
import { AlertCircle, X } from 'lucide-react';
import { useAperturaAnimada } from '../../hooks/useAperturaAnimada';
import type { MotivoDelClip } from './motivoDelClip';

export function AvisoDelClip({
  motivo,
  onReintentar,
  onCerrar,
}: {
  /** null = no hay nada que avisar (sale con su transición). */
  motivo: MotivoDelClip | null;
  onReintentar?: () => void;
  onCerrar: () => void;
}) {
  const abierto = motivo !== null;
  const { montado, visible } = useAperturaAnimada(abierto, 150);
  // El último motivo, para seguir mostrándolo mientras sale.
  const ultimo = useRef(motivo);
  if (motivo) ultimo.current = motivo;
  const mostrado = motivo ?? ultimo.current;
  if (!montado || !mostrado) return null;
  return (
    <div
      role="status"
      aria-live="polite"
      className={`pointer-events-auto flex max-w-[min(560px,calc(100vw-32px))] items-start gap-2.5 rounded-lg bg-white py-2.5 pl-3 pr-2 text-[12.5px] text-brand-black shadow-md ring-1 ring-black/[0.06] transition-[opacity,transform,filter] ease-out ${
        visible
          ? 'opacity-100 duration-300 motion-safe:translate-y-0 motion-safe:blur-0'
          : abierto
            ? 'opacity-0 duration-300 motion-safe:translate-y-3 motion-safe:blur-[4px]'
            : 'opacity-0 duration-150 motion-safe:-translate-y-3 motion-safe:blur-[4px]'
      }`}
    >
      <AlertCircle className="mt-0.5 h-4 w-4 shrink-0 text-error" aria-hidden="true" />
      <div className="min-w-0 flex-1">
        <p className="font-semibold">{mostrado.titulo}</p>
        <p className="mt-0.5 text-brand-black/70">{mostrado.motivo}</p>
      </div>
      {mostrado.reintentable && onReintentar && (
        <button
          type="button"
          onClick={onReintentar}
          className="shrink-0 rounded-md px-3 py-1.5 text-[12px] font-semibold text-brand-primary transition-[background-color,transform] duration-150 ease-out hover:bg-[rgb(var(--brand-primary-rgb)_/_0.085)] active:scale-[0.96]"
        >
          Reintentar
        </button>
      )}
      <button
        type="button"
        onClick={onCerrar}
        aria-label="Cerrar aviso"
        className="shrink-0 rounded-md p-1.5 text-brand-black/45 transition-[background-color,color,transform] duration-150 ease-out hover:bg-black/[0.04] hover:text-brand-black/70 active:scale-[0.96]"
      >
        <X className="h-3.5 w-3.5" />
      </button>
    </div>
  );
}
